import { a3Copy } from "@/components/assignments/a3-copy";
/**
 * One material row's save engine. A typed fact saves when it is left or Enter
 * is pressed, and Escape puts back the saved value; a chosen fact (a class, a
 * Drive file) saves when it is chosen. Each save carries the version it was
 * made against, and the material it returns becomes the row's base, so a
 * second save never meets the first as a conflict.
 *
 * Once a material is published its content is never rewritten: a change to
 * its title, type or file is a correction, which gives the placement a new
 * material and learners an "Updated" date. Its class is a plain change.
 *
 * Saves and the row's one act, Remove, run one after another.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { ApiError } from "@/lib/api/client";
import { currentOnConflict } from "@/lib/api/conflict";
import {
  courseMaterialsApi,
  type Material,
  type ResourcePlacement,
} from "@/lib/api/courseMaterials";

import {
  changesFrom,
  draftOf,
  sourceContent,
  typedFacts,
  type DriveFile,
  type MaterialContent,
  type MaterialDraft,
  type MaterialFact,
  type MaterialItem,
} from "./material-facts";
import { placementsKey, type MaterialLibrary } from "./use-material-library";

export type SaveState =
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "failed"; reason: string | null; retry: boolean }
  | { kind: "conflict"; theirs: Material };

interface Status {
  fact: MaterialFact;
  state: SaveState;
}

/** What a row act left to say: a refusal, or the state it found. */
export interface ActNote {
  text: string;
  alert: boolean;
}

type DraftKey = keyof MaterialDraft;

const KEYS_OF: Record<MaterialFact, DraftKey[]> = {
  title: ["title"],
  type: ["type"],
  source: ["link", "drive"],
  class: ["unitId"],
};

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

const reasonOf = (error: unknown, fallback: string) => (error instanceof ApiError ? error.message : fallback);

export function useMaterialSave(item: MaterialItem, library: MaterialLibrary) {
  const queryClient = useQueryClient();
  const { courseId } = library;
  const [draft, setDraft] = useState(() => draftOf(item.material));
  const [status, setStatus] = useState<Status | null>(null);
  const [note, setNote] = useState<ActNote | null>(null);
  const [busy, setBusy] = useState(false);
  const base = useRef(item.material);
  const placement = useRef<ResourcePlacement | null>(item.placement);
  const typed = useRef(draft);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const pending = useRef(0);
  // A newer version is showing: nothing is sent until the editor chooses.
  const deciding = useRef(false);

  const edit = (next: MaterialDraft) => {
    typed.current = next;
    setDraft(next);
  };

  // A newer placement from the server replaces the one the row holds.
  useEffect(() => {
    const current = placement.current;
    if (!item.placement) {
      if (pending.current === 0) placement.current = null;
      return;
    }
    if (!current || current.id !== item.placement.id || item.placement.revision >= current.revision) {
      placement.current = item.placement;
    }
  }, [item.placement]);

  // A newer material fills the fields only while nothing typed here is
  // unsaved; otherwise the next save meets it as a conflict. An older read
  // never replaces a save.
  useEffect(() => {
    if (!(Date.parse(item.material.updated_at) > Date.parse(base.current.updated_at)) || pending.current > 0) return;
    if (typedFacts(typed.current, base.current).length > 0) return;
    base.current = item.material;
    edit(draftOf(item.material));
  }, [item.material]);

  /** The saved material becomes the base; a fact sent or left alone takes its saved value. */
  const adopt = (saved: Material, sent: MaterialDraft, sentFacts: ReadonlySet<MaterialFact>) => {
    const before = draftOf(base.current);
    const after = draftOf(saved);
    base.current = saved;
    let next = typed.current;
    for (const fact of Object.keys(KEYS_OF) as MaterialFact[]) {
      for (const key of KEYS_OF[fact]) {
        const untouched = same(typed.current[key], sent[key]);
        const settled = sentFacts.has(fact) || same(sent[key], before[key]);
        if (untouched && settled && !same(next[key], after[key])) next = { ...next, [key]: after[key] };
      }
    }
    if (next !== typed.current) edit(next);
  };

  const enqueue = <T,>(job: () => Promise<T>): Promise<T> => {
    pending.current += 1;
    const run = queue.current.then(job).finally(() => {
      pending.current -= 1;
    });
    queue.current = run.catch(() => undefined);
    return run;
  };

  /**
   * Write content and class. Content on a published material is a correction;
   * everything else patches the material at the version it was read.
   */
  const write = async (
    shown: MaterialFact,
    content: MaterialContent,
    unitId: string | null | undefined,
    sentFacts: ReadonlySet<MaterialFact>,
  ) => {
    const sent = typed.current;
    setStatus({ fact: shown, state: { kind: "saving" } });
    try {
      let saved = base.current;
      if (Object.keys(content).length > 0) {
        const published = placement.current?.publication_status === "published" ? placement.current : null;
        if (published) {
          const result = await courseMaterialsApi.correctMaterial(courseId, saved.id, {
            ...content,
            revision: published.revision,
            expected_updated_at: saved.updated_at,
          });
          placement.current = result.placement;
          library.adoptCorrection(saved.id, result);
          saved = result.material;
        } else {
          saved = await courseMaterialsApi.patchMaterial(courseId, saved.id, {
            ...content,
            expected_updated_at: saved.updated_at,
          });
        }
      }
      if (unitId !== undefined) {
        saved = await courseMaterialsApi.patchMaterial(courseId, saved.id, {
          unit_id: unitId,
          expected_updated_at: saved.updated_at,
        });
      }
      adopt(saved, sent, sentFacts);
      setStatus({ fact: shown, state: { kind: "saved" } });
      library.adoptMaterial(saved);
    } catch (error) {
      const theirs = currentOnConflict<Material>(error);
      deciding.current = theirs !== null;
      if (theirs) void queryClient.invalidateQueries({ queryKey: placementsKey(courseId) });
      setStatus({
        fact: shown,
        state: theirs
          ? { kind: "conflict", theirs }
          : error instanceof ApiError && (error.status === 422 || error.status === 409)
            ? { kind: "failed", reason: error.message, retry: false }
            : { kind: "failed", reason: null, retry: true },
      });
    }
  };

  /**
   * Send what changed. A fact left with nothing of its own to save sends
   * nothing, unless `any` (Retry, Keep my edits) asks for every change.
   */
  const send = async (fact: MaterialFact, any = false) => {
    if (deciding.current) return;
    const { content, unitId, facts } = changesFrom(typed.current, base.current, item.role);
    if (facts.size === 0) {
      setStatus((current) => (current?.state.kind === "failed" ? null : current));
      return;
    }
    const own = facts.has(fact);
    if (!own && !any) return;
    await write(own ? fact : [...facts][0], content, unitId, facts);
  };

  const save = (fact: MaterialFact, any = false) => {
    void enqueue(() => send(fact, any));
  };

  const setField = <K extends DraftKey>(key: K, value: MaterialDraft[K]) => {
    edit({ ...typed.current, [key]: value });
    if (key === "unitId") save("class");
  };

  /** A pasted link is a link, not the Drive file it replaces. */
  const setLink = (link: string) => edit({ ...typed.current, link, drive: null });

  /** A Drive file chosen for the row saves at once, as its source. */
  const chooseFile = (link: string, drive: DriveFile) => {
    edit({ ...typed.current, link, drive });
    void enqueue(async () => {
      if (deciding.current) return;
      const wasDrive = base.current.google_drive_file_id !== null;
      await write("source", sourceContent(link, drive, wasDrive), undefined, new Set<MaterialFact>(["source"]));
    });
  };

  const restore = (fact: MaterialFact) => {
    const saved = draftOf(base.current);
    let next = typed.current;
    for (const key of KEYS_OF[fact]) next = { ...next, [key]: saved[key] };
    edit(next);
    setStatus((current) => (current?.fact === fact && current.state.kind === "failed" ? null : current));
  };

  const commit = (fact: MaterialFact) => ({
    onBlur: () => save(fact),
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        save(fact);
      } else if (event.key === "Escape") {
        restore(fact);
      }
    },
  });

  // Keeping my edits keeps only the facts typed here; every other fact takes
  // their version, so their change to a fact left alone is not undone.
  const keepMine = () => {
    if (status?.state.kind !== "conflict") return;
    const { theirs } = status.state;
    const theirDraft = draftOf(theirs);
    let merged = theirDraft;
    for (const fact of typedFacts(typed.current, base.current)) {
      for (const key of KEYS_OF[fact]) merged = { ...merged, [key]: typed.current[key] };
    }
    const fact = status.fact;
    base.current = theirs;
    deciding.current = false;
    edit(merged);
    setStatus(null);
    void enqueue(async () => {
      // A correction carries the placement's revision, so read the one their save left.
      await queryClient.refetchQueries({ queryKey: placementsKey(courseId) });
      const fresh = queryClient
        .getQueryData<ResourcePlacement[]>(placementsKey(courseId))
        ?.find((candidate) => candidate.id === placement.current?.id);
      if (fresh) placement.current = fresh;
      await send(fact, true);
    });
  };

  const loadTheirs = () => {
    if (status?.state.kind !== "conflict") return;
    const { theirs } = status.state;
    base.current = theirs;
    deciding.current = false;
    edit(draftOf(theirs));
    setStatus(null);
    library.adoptMaterial(theirs);
  };

  /** A row act in turn with the saves. Its failure is said in the row. */
  const act = (job: () => Promise<void>, failure: string) => {
    setNote(null);
    setBusy(true);
    void enqueue(async () => {
      try {
        await job();
      } catch (error) {
        setNote({ text: reasonOf(error, failure), alert: true });
      } finally {
        setBusy(false);
      }
    });
  };

  const remove = () => act(async () => {
    await courseMaterialsApi.deleteMaterial(courseId, base.current.id);
    library.drop(base.current.id);
    toast.success(a3Copy.draftRemoved);
  }, a3Copy.couldNotRemoveThisMaterial);

  return {
    draft,
    setField,
    setLink,
    chooseFile,
    commit,
    keepMine,
    loadTheirs,
    remove,
    busy,
    note,
    retry: () => {
      if (status) save(status.fact, true);
    },
    state: status?.state ?? null,
    /** A refused save's reason, under the fact that was saved. */
    failureOf: (fact: MaterialFact): string | null => (
      status?.fact === fact && status.state.kind === "failed" ? status.state.reason : null
    ),
  };
}
